import { CancelIcon } from "@novel/shared/icons";
import { Box } from "@novel/shared/components/Box";
import { CappedDimensionMedia } from "@novel/shared/components/CappedDimensionMedia";
import { Countdown } from "@novel/shared/components/Countdown";
import { SectionTitleText } from "@novel/shared/components/Primitives";
import { useIsTabletWidth } from "@novel/shared/hooks/useIsMobile";
import { useWindowEventEffect } from "@novel/shared/hooks/useWindowEventEffect";
import { ITokenWithMetadataAndCollection } from "@novel/shared/interfaces/Token";
import { accepted3dFileTypes, IPFS_GATEWAY_PREFIX } from "@novel/shared/utils/appConstants";
import { checkIsUnrevealed } from "@novel/shared/utils/checkIsUnrevealed";
import { getBlockchainTokenLink, getIsPolygon, getSecondaryMarketTokenLink, getTokenVideoSrc } from "@novel/shared/utils/cryptoLinkUtils";
import { openInNewTab } from "@novel/shared/utils/locationUtils";
import { Button } from "@shopify/polaris";
import React, { useCallback, useState } from "react";
import { IBenefits } from "@novel/shared/interfaces/Discounts";
import { Row } from "rsuite";
import cn from "classnames";
import { typedFrontendGalleryApi } from "@customer-gallery/utils/typedFrontendGalleryApi";
import { useAsyncCallback } from "@novel/shared/utils/useAsyncCallback";
import { AttributeList, DiscountList, GatedUrlList, PageBenefitList, PageDiscountAllList, PageDiscountList, PageGatedUrlsList, PageProductsList, PageProductsTableList, PageUrlsList, ProductsList, Links } from "./Lists";
import { getContentType } from "@novel/shared/utils/getContentType";
import { useIsSuperUserSelector } from "@customer-gallery/redux/reducers/auth";
interface INFTPreview {
  readonly revealed: boolean;
  readonly mobile: boolean;
  readonly claimedToken: ITokenWithMetadataAndCollection;
}
declare global {
  //   eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "model-viewer": MyElementAttributes;
    }
    interface MyElementAttributes {
      src: string | undefined;
    }
  }
}
function getPosterFromMetadata(props: INFTPreview): string {
  const {
    tokenMetadata,
    collectionSmartContract
  } = props.claimedToken;
  if (tokenMetadata?.cloudImage) return tokenMetadata.cloudImage;
  if (tokenMetadata?.ipfsImageCid) return `${IPFS_GATEWAY_PREFIX}${tokenMetadata.ipfsImageCid}`;
  if (collectionSmartContract.collectionMetadata.cloudImage) return collectionSmartContract.collectionMetadata.cloudImage;
  if (collectionSmartContract.collectionMetadata.ipfsImageCid) return `${IPFS_GATEWAY_PREFIX}${collectionSmartContract.collectionMetadata.ipfsImageCid}`;
  return "";
}
function NFTPreviewContainer({
  children
}: {
  readonly children: JSX.Element;
}): JSX.Element {
  return <div className="NFTPreviewer">
            <div className="NFTPreviewInnerCover">{children}</div>
        </div>;
}
function NFTPreview(props: INFTPreview): JSX.Element {
  const {
    revealed,
    mobile,
    claimedToken
  } = props;
  const {
    tokenMetadata,
    collectionSmartContract
  } = claimedToken;
  const [contentType, setContentType] = useState("");
  const getFileContentType = useAsyncCallback(async () => {
    const videoSrc = getTokenVideoSrc(claimedToken);
    if (videoSrc) {
      const content = await getContentType(videoSrc);
      setContentType(content);
    }
  }, [contentType, setContentType, claimedToken]);
  if (tokenMetadata?.cloudVideo || tokenMetadata?.animationUrl) {
    getFileContentType();
    return <React.Fragment>
                {accepted3dFileTypes.includes(contentType) ? <Box justifyContent={"center"} style={{
        width: "100%",
        height: "370px"
      }} alignItems="center">
                        <model-viewer camera-controls touch-action="pan-y" auto-rotate src={getTokenVideoSrc(claimedToken)} />
                    </Box> : <CappedDimensionMedia className={cn("MainNFTImage", !revealed && "unrevealed")} type="video" controls muted={mobile} autoPlay={mobile} src={getTokenVideoSrc(claimedToken)} poster={getPosterFromMetadata(props)} />}
            </React.Fragment>;
  }
  if (tokenMetadata?.cloudImage || tokenMetadata?.ipfsImageCid || collectionSmartContract.collectionMetadata.cloudImage || collectionSmartContract.collectionMetadata.ipfsImageCid) {
    let src = "";
    if (tokenMetadata?.cloudImage) {
      src = tokenMetadata?.cloudImage;
    } else if (tokenMetadata?.ipfsImageCid) {
      src = tokenMetadata.ipfsImageCid.startsWith("https") ? tokenMetadata.ipfsImageCid : `${IPFS_GATEWAY_PREFIX}${tokenMetadata.ipfsImageCid}`;
    } else if (collectionSmartContract.collectionMetadata.cloudImage) {
      src = collectionSmartContract.collectionMetadata.cloudImage;
    } else if (collectionSmartContract.collectionMetadata.ipfsImageCid) {
      src = `${IPFS_GATEWAY_PREFIX}${collectionSmartContract.collectionMetadata.ipfsImageCid}`;
    }
    return <img className={cn("MainNFTImage", !revealed && "unrevealed")} src={src} />;
  }
  return <div className="no-content-background">
            <div className="imageCircular-card rounded-full gradientBg" />
            <span className="text-no-content">Content not available yet</span>
        </div>;
}
interface IClaimedModalProps {
  readonly claimedToken: ITokenWithMetadataAndCollection;
  readonly onClose: () => void;
  readonly setPrevClaimedToken?: () => void;
  readonly setNextClaimedToken?: () => void;
  readonly discounts?: IBenefits[];
}
export function ClaimedModal({
  claimedToken,
  onClose,
  setPrevClaimedToken,
  setNextClaimedToken,
  discounts
}: IClaimedModalProps): JSX.Element {
  const [modalOpen, setModalOpen] = useState(true);
  const isMobile = useIsTabletWidth();
  const [attributeMore, setAttributeMore] = useState(false);
  const [productMore, setProductMore] = useState(false);
  const [urlsMore, setUrlsMore] = useState(false);
  const [discountMore, setDiscountMore] = useState(false);
  const isSuperUser = useIsSuperUserSelector();
  const [refreshLoad, setRefreshLoad] = useState(false);
  const [pageNow, setPageNow] = useState<"ALL" | "PRODUCT" | "URL" | "DISCOUNT" | "BENEFIT">("ALL");
  const ToggleAttribute = useCallback(() => setAttributeMore(attributeprev => !attributeprev), []);
  const ToggleUrls = useCallback(() => setUrlsMore(urlsMoreprev => !urlsMoreprev), []);
  const ToggleProduct = useCallback(() => setProductMore(productMoreprev => !productMoreprev), []);
  const ToggleDiscount = useCallback(() => setDiscountMore(discountMoreprev => !discountMoreprev), []);
  const TogglePage = useCallback(page => setPageNow(page), []);
  // const ToggleAttribute = useCallback(() => setAttributeMore(attributeprev => !attributeprev), [])

  // delaying to allow animation out
  const finalHandleOnClose = useCallback(() => {
    setModalOpen(false);
    setTimeout(onClose, 100);
  }, [onClose]);
  useWindowEventEffect("keydown", e => {
    switch (e.key) {
      case "Escape":
        {
          finalHandleOnClose();
          break;
        }
      case "ArrowLeft":
      case "ArrowUp":
        {
          if (setPrevClaimedToken) {
            setPrevClaimedToken();
          }
          break;
        }
      case "ArrowRight":
      case "ArrowDown":
        {
          if (setNextClaimedToken) {
            setNextClaimedToken();
          }
          break;
        }
    }
  });
  const refreshToken = useAsyncCallback(async () => {
    setRefreshLoad(true);
    await typedFrontendGalleryApi.postReq("/api/customer/refresh-metadata", {
      reqBody: {
        contractAddress: claimedToken.collectionSmartContract.address,
        chain: claimedToken.collectionSmartContract.blockchain,
        tokenId: claimedToken.tokenId
      }
    });
    setRefreshLoad(false);
  }, [claimedToken.collectionSmartContract.address, claimedToken.collectionSmartContract.blockchain, claimedToken.tokenId]);
  const IPFSMetadata = claimedToken.collectionSmartContract.baseTokenIpfsCid && claimedToken.tokenMetadata?.trueIndex ? `${IPFS_GATEWAY_PREFIX}${claimedToken.collectionSmartContract.baseTokenIpfsCid}/${claimedToken.tokenMetadata.trueIndex}.json` : claimedToken.tokenUri;
  const isOnPolygon = getIsPolygon(claimedToken.collectionSmartContract.blockchain);
  const isOnSolana = claimedToken.collectionSmartContract.blockchain === "solana";
  const openSeaLink = getSecondaryMarketTokenLink(claimedToken.collectionSmartContract.blockchain, claimedToken.collectionSmartContract.address!, claimedToken.tokenId!);
  const blockchainLink = getBlockchainTokenLink(claimedToken.collectionSmartContract.blockchain, claimedToken.collectionSmartContract.address!, claimedToken.tokenId!);
  const isUnrevealed = checkIsUnrevealed(claimedToken.collectionSmartContract.collectionMetadata);
  const filteredNFTItem = discounts?.find(item => item.collectionAddress === claimedToken.collectionSmartContract.address);
  const attributeAll = attributeMore ? claimedToken.tokenMetadata?.attributes : isMobile ? claimedToken.tokenMetadata?.attributes.slice(0, 6) : claimedToken.tokenMetadata?.attributes.slice(0, 4);
  const productAll = productMore ? filteredNFTItem?.products : isMobile ? filteredNFTItem?.products?.slice(0, 6) : filteredNFTItem?.products?.slice(0, 4);
  const gatedUrls = urlsMore ? filteredNFTItem?.gatedUrls : filteredNFTItem?.gatedUrls?.slice(0, 2);
  const discountsAll = discountMore ? filteredNFTItem?.discounts : filteredNFTItem?.discounts?.slice(0, 2);
  const showAllContent = filteredNFTItem?.products && filteredNFTItem?.products.length > 0 || filteredNFTItem?.gatedUrls && filteredNFTItem?.gatedUrls.length > 0 || filteredNFTItem?.discounts && filteredNFTItem?.discounts.length > 0;
  return <React.Fragment>
            <div className="pointer-events-auto z-40">
                <div className="blurUp">
                    <div className="opacity-100">
                        <div className="gBlurUpper" onClick={finalHandleOnClose} />
                    </div>
                    <span className="insideBox">
                        <div className="middleBoxIn" style={{
            alignItems: `${!showAllContent ? "center" : "flex-start"}`
          }}>
                            <div className="closeModalBoxMobile" onClick={finalHandleOnClose}>
                                <div className="white-background-circular">
                                    <CancelIcon height={34} width={34} />
                                </div>
                            </div>
                            <div className={cn("flexer-col", "animated-modal", !modalOpen && "out")} style={{
              gap: "1rem"
            }}>
                                <div className="ModalBoxAreaMain">
                                    <NFTPreviewContainer>
                                        <NFTPreview claimedToken={claimedToken} mobile={isMobile} revealed={!isUnrevealed} />
                                    </NFTPreviewContainer>

                                    <div className="MiddleAligner" />
                                    <div className="ModalDetailBoxStart">
                                        <span className="cancel-icon-main">
                                            <CancelIcon onClick={finalHandleOnClose} style={{
                      cursor: "pointer"
                    }} />
                                        </span>
                                        <div className="ModalDetailBoxStartInsider">
                                            <div className="flex mb-1.5">
                                                <div className="collection-text">
                                                    {claimedToken.collectionSmartContract.collectionMetadata.title}
                                                </div>
                                            </div>
                                            <div className="text-area-full-width">
                                                {(isUnrevealed || claimedToken.tokenMetadata?.title || claimedToken.collectionSmartContract.collectionMetadata.title || claimedToken.tokenId) && <SectionTitleText className="nft-name-tokenId">
                                                        {isUnrevealed ? `${claimedToken.collectionSmartContract.collectionMetadata.title} Unrevealed #${claimedToken.tokenId}` : claimedToken.tokenMetadata?.title ? claimedToken.tokenMetadata?.title : `${claimedToken.collectionSmartContract.collectionMetadata.title} #${claimedToken.tokenId}`}
                                                    </SectionTitleText>}
                                                {isSuperUser && <div>
                                                        <Button loading={refreshLoad} onClick={refreshToken}>
                                                            Refresh
                                                        </Button>
                                                    </div>}
                                            </div>
                                            {isUnrevealed && <Countdown prependStr="Collection Reveals" countdownTo={claimedToken.collectionSmartContract.collectionMetadata.revealingOn!} />}
                                            <AttributeList attributeAll={attributeAll} attributeMore={attributeMore} claimedToken={claimedToken} ToggleAttribute={ToggleAttribute} />

                                            <ProductsList productAll={productAll} filteredNFTItem={filteredNFTItem} productMore={productMore} ToggleProduct={ToggleProduct} />

                                            <DiscountList discountsAll={discountsAll} filteredNFTItem={filteredNFTItem} discountMore={discountMore} ToggleDiscount={ToggleDiscount} />

                                            <GatedUrlList gatedUrls={gatedUrls} filteredNFTItem={filteredNFTItem} urlsMore={urlsMore} ToggleUrls={ToggleUrls} />

                                            <hr className="spacing-sm" />

                                            <div className="marketplace_areas">
                                                {(openSeaLink || blockchainLink) && <Box columnGap={1}>
                                                        {openSeaLink && <button onClick={() => openInNewTab(openSeaLink)} className="full-button" style={{
                          width: "149px",
                          height: "28px",
                          padding: "0px"
                        }}>
                                                                View on OpenSea
                                                            </button>}
                                                        {blockchainLink && <button onClick={() => openInNewTab(blockchainLink)} className="full-button" style={{
                          width: "149px",
                          height: "28px",
                          padding: "0px"
                        }}>
                                                                View on
                                                                {isOnPolygon ? "PolygonScan" : isOnSolana ? "Solana Exp." : "EtherScan"}
                                                            </button>}
                                                    </Box>}
                                            </div>

                                            {(isMobile || !showAllContent) && <div className="grey-button-place">
                                                    <Links isUnrevealed={isUnrevealed} claimedToken={claimedToken} IPFSMetadata={IPFSMetadata} />
                                                </div>}
                                        </div>
                                    </div>
                                </div>
                                {showAllContent && <div className="BenefitsBoxStart">
                                        <div style={{
                  width: "100%"
                }}>
                                            <Row className="show-grid">
                                                {pageNow === "ALL" && <React.Fragment>
                                                        <PageProductsList filteredNFTItem={filteredNFTItem} TogglePage={TogglePage} />
                                                        <PageGatedUrlsList filteredNFTItem={filteredNFTItem} TogglePage={TogglePage} />
                                                        <PageDiscountList filteredNFTItem={filteredNFTItem} TogglePage={TogglePage} />
                                                    </React.Fragment>}
                                                {pageNow === "BENEFIT" && <PageBenefitList filteredNFTItem={filteredNFTItem} TogglePage={TogglePage} />}
                                                {pageNow === "PRODUCT" && <PageProductsTableList filteredNFTItem={filteredNFTItem} TogglePage={TogglePage} />}

                                                {pageNow === "URL" && <PageUrlsList filteredNFTItem={filteredNFTItem} TogglePage={TogglePage} />}

                                                {pageNow === "DISCOUNT" && <PageDiscountAllList filteredNFTItem={filteredNFTItem} TogglePage={TogglePage} />}
                                            </Row>
                                            {pageNow === "ALL" && <div className="end-button-place">
                                                    <Links isUnrevealed={isUnrevealed} IPFSMetadata={IPFSMetadata} claimedToken={claimedToken} />
                                                </div>}
                                        </div>
                                    </div>}
                            </div>
                        </div>
                    </span>
                </div>
            </div>
            {/* <div className="closeModalBox" onClick={finalHandleOnClose}>
                <div className="opacity-100">
                    <XCircleIcon height={34} width={34} className="text-white" />
                </div>
             </div> */}
            <style global jsx>{`
                .blurUp > div {
                    height: 100%;
                }
                .marketplace_areas {
                    margin-bottom: 1rem;
                    margin-top: 1rem;
                }
                .box-col-spacing {
                    margin: 10px;
                    width: 48%;
                }
                .box-container-section {
                    margin-bottom: 3rem;
                    width: 100%;
                    margin-top: 3rem;
                    display: none;
                }
                .product-name-benefit {
                    font-weight: 400;
                    font-size: 11px;
                    color: #5c5f62;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    width: 100px;
                    text-align: center;
                }
                .grey-button-place {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: wrap;
                }
                .end-button-place {
                    display: flex;
                    justify-content: flex-end;
                    margin-bottom: 20px;
                    margin-left: 20px;
                    margin-right: 20px;
                }
                .subtext-heading-modal {
                    font-weight: 700;
                    font-size: 16px;
                    margin-bottom: 10px;
                }
                .box-section-area-sm {
                    margin-top: 20px;
                    margin-bottom: 20px;
                    display: none;
                }
                .spacing-sm {
                    background: #e1e3e5;
                    width: 100%;
                    display: none;
                }
                .benefit-content-products {
                    margin-top: 20px;
                    display: grid;
                    column-gap: 3rem;
                    row-gap: 1.5rem;
                    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
                }
                .url-gated-list-area {
                    display: flex;
                    justify-content: space-between;
                    padding: 12px 0px;
                    border-bottom: 1px solid #e1e3e5;
                    align-items: center;
                    overflow: hidden;
                }
                .url-gated-list-area-sm {
                    display: flex;
                    justify-content: center;
                    padding: 12px 0px;
                    border-bottom: 1px solid #e1e3e5;
                    align-items: flex-start;
                    flex-direction: column;
                    overflow: hidden;
                }
                .flex-block-switch {
                    display: flex;
                }
                .grey-button {
                    background: #f7f7f7;
                    justify-content: center;
                    display: flex;
                    color: #fff;
                    padding: 0px;
                    font-weight: 500;
                    font-size: 12px;
                    width: 149px;
                    height: 28px;
                    align-items: center;
                    border-radius: 20px;
                    color: #959595;
                    margin: 5px;
                }
                .url-idea-name {
                    font-weight: 400;
                    font-size: 13px;
                    color: #5c5f62;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    width: 90%;
                }
                .discount-idea-name {
                    font-weight: 400;
                    font-size: 13px;
                    color: #5c5f62;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    width: 40%;
                }
                .discount-idea-name-sm {
                    font-weight: 400;
                    font-size: 13px;
                    color: #5c5f62;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    width: 100%;
                }
                .discount-idea-code {
                    font-weight: 400;
                    font-size: 13px;
                    color: #5c5f62;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    width: 100%;
                }
                .discount-idea-code-sm {
                    font-weight: 400;
                    font-size: 13px;
                    color: #5c5f62;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    width: 100%;
                }
                .white-background-circular {
                    width: 36px;
                    height: 36px;
                    background: #ffffff;
                    border-radius: 50%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    cursor: pointer;
                }
                .code-copier-area {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 45%;
                }
                .code-copier-area-sm {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                }
                .copied-area {
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    align-items: center;
                    padding: 10px;
                    background: #f7f7f7;
                    border-radius: 100px;
                    cursor: pointer;
                    width: fit-content;
                }
                .box-area-benefits {
                    padding: 30px;
                }
                .grey-button-see-more {
                    display: none;
                }
                .product-box {
                    display: flex;
                    flex-direction: column;
                    width: fit-content;
                    align-items: center;
                }
                .table-cell-image-container {
                    margin-right: 0px;
                    width: 100px;
                    height: 100px;
                    margin-bottom: 12px;
                    box-shadow: none;
                }
                .line-spacer-main {
                    height: 20px;
                }
                .cancel-icon-main {
                    margin-left: auto;
                    margin-right: 8px;
                    margin-top: 3px;
                    z-index: 1;
                }
                .Polaris-Button--plain .Polaris-Button__Content {
                    font-size: 11px;
                    font-weight: 500;
                }
                .text-area-full-width {
                    width: 100%;
                    justify-content: space-between;
                    display: flex;
                }
                .button-readmore {
                    border: 1px solid #cccccc;
                    border-radius: 4px;
                    background: #ffffff;
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    width: 52px;
                    height: 81px;
                }
                .table-header-smaple {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    width: 100%;
                }
                .benefit-content-text {
                    font-weight: 400;
                    font-size: 13px;
                    line-height: 25px;
                    color: #5c5f62;
                }
                .no-content-background {
                    position: relative;
                    flex-direction: column;
                    height: 100%;
                    width: 100%;
                    display: flex;
                    min-height: 380px;
                    justify-content: center;
                    align-items: center;
                    background: linear-gradient(
                            163.05deg,
                            rgb(221 255 0 / 20%) 17.45%,
                            rgb(110 215 181 / 20%) 64.25%,
                            rgb(90 219 255 / 20%) 121.94%
                        ),
                        #fff;
                }
                .attribute-box-area {
                    margin-right: 1rem;
                }
                .box-container-benefit {
                }
                .Attr_BoxArea {
                    width: 100%;
                    display: flex;
                    padding: 0px;
                    align-items: start;
                    justify-content: flex-start;
                    flex-wrap: wrap;
                    margin-bottom: -10px;
                }
                .imageCircular-smallcard {
                    height: 20px;
                    margin-right: 6px;
                    width: 20px;
                }
                .AttrBoxEach {
                    background: radial-gradient(
                        100% 100% at 0% 50%,
                        rgba(60, 66, 82, 0.024) 0%,
                        rgba(60, 66, 82, 0.03) 100%
                    );
                    border-radius: 15px;
                    display: flex;
                    flex-direction: column;
                    margin: 0px 10px 10px 0px;
                    padding: 8px 10px;
                    text-align: left;
                    z-index: 1;
                }
                .AttrKeyEach {
                    color: rgba(60, 66, 82, 0.5);
                    font-size: 11px;
                    font-style: normal;
                    font-weight: 800;
                    padding-right: 10px;
                    text-align: left;
                    transition: all 0.125s ease 0s;
                }
                .AttrValueEach {
                    color: rgba(60, 66, 82, 0.5);
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    max-width: 200px;
                    overflow: hidden;
                    text-align: left;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .box_main_area {
                    width: 100%;
                    display: flex;
                    padding: 0px;
                    align-items: flex-start;
                    justify-content: space-between;
                    flex-direction: column !important;
                }
                .blockage_divider {
                    background-color: rgba(60, 66, 82, 0.016);
                    border-radius: 1px;
                    height: 2px;
                    margin-bottom: 19px;
                    margin-top: 0px;
                    width: 150%;
                }
                .HeadingBoldMain {
                    -webkit-line-clamp: 1;
                    color: rgb(37, 41, 46);
                    font-size: 20px;
                    font-style: normal;
                    font-weight: 700;
                    padding: 0px 0px 12px;
                    text-align: left;
                    text-overflow: ellipsis;
                }
                .trait_value {
                    white-space: nowrap;
                    font-weight: 700;
                    font-size: 13px;
                    color: #000000;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    width: 90px;
                }
                .trait_name {
                    white-space: nowrap;
                    font-weight: 700;
                    font-size: 11px;
                    color: #959595;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    width: 90px;
                }
                .DetailLightTextArea {
                    color: rgb(157, 160, 168);
                    font-size: 16px;
                    font-style: normal;
                    font-weight: normal;
                    letter-spacing: 0.4px;
                    line-height: 1.5625;
                    padding-right: 10px;
                    text-align: left;
                    transition: all 0.125s ease 0s;
                    width: 100%;
                    overflow-wrap: break-word;
                }
                .circularIcon {
                    height: 20px;
                    margin-right: 5px;
                    margin-top: 2px;
                    width: 20px;
                }
                .buttonForMarketplace {
                    -webkit-box-align: center;
                    align-items: center;
                    background: rgb(255, 255, 255);
                    border-radius: 15px;
                    box-shadow: rgb(37 41 46 / 10%) 0px 4px 12px;
                    box-sizing: content-box;
                    color: rgba(60, 66, 82, 0.8);
                    cursor: pointer;
                    display: flex;
                    height: 28px;
                    margin-bottom: 10px;
                    margin-right: 10px;
                    padding: 0px 10px 2px 6px;
                    font-size: 12px;
                    font-weight: 800;
                    transition: all 0.125s ease 0s;
                    user-select: none;
                    will-change: transform;
                }
                .MainNFTImage {
                    color: rgba(0, 0, 0, 0);
                    height: 100%;
                    width: 100%;
                    border-radius: 10px;
                    pointer-events: auto;
                    object-fit: cover;
                }
                .unrevealed {
                    filter: blur(10px);
                    -webkit-filter: blur(10px);
                    transform: scale(1.05);
                }
                .NFT_name_info {
                    -webkit-line-clamp: 1;
                    color: rgb(37, 41, 46);
                    font-size: 26px;
                    font-style: normal;
                    font-weight: 800;
                    padding: 0px;
                    text-align: left;
                    text-overflow: ellipsis;
                    margin: 0px;
                    line-height: 1.175em;
                    max-width: 666px;
                }
                .collection-text {
                    align-self: center;
                    color: #000000;
                    font-weight: 400;
                    font-size: 13px;
                    letter-spacing: 0.8px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    text-transform: uppercase;
                    transition: all 0.125s ease 0s;
                    white-space: nowrap;
                    padding-left: 4px;
                }
                .nft-name-tokenId {
                    font-weight: 600;
                    font-size: 28px;
                    color: #000000;
                }
                .ModalDetailBoxStartInsider {
                    align-items: flex-start;
                    box-sizing: border-box;
                    padding: 24px 24px 0px;
                    width: 100%;
                    -webkit-box-align: center;
                    display: flex;
                    -webkit-box-pack: start;
                    justify-content: flex-start;
                    flex-direction: column !important;
                    margin-top: -40px;
                    margin-bottom: 10px;
                }
                .ModalDetailBoxStart {
                    align-items: flex-start;
                    background: white;
                    border-radius: 10px;
                    box-shadow: rgb(0 0 0 / 40%) 0px 10px 30px;
                    box-sizing: border-box;
                    display: flex;
                    flex-direction: column;
                    max-height: 80vh;
                    max-width: 653px;
                    min-width: 653px;
                    opacity: 1;
                    overflow-x: hidden;
                    pointer-events: auto;
                    transition: all 0.125s ease 0s;
                    width: 100%;
                    min-height: 370px;
                }
                .BenefitsBoxStart {
                    align-items: flex-start;
                    background: white;
                    border-radius: 10px;
                    box-shadow: rgb(0 0 0 / 40%) 0px 10px 30px;
                    box-sizing: border-box;
                    display: flex;
                    flex-direction: column;
                    max-height: 80vh;
                    max-width: 1053px;
                    min-width: 1053px;
                    opacity: 1;
                    overflow-x: hidden;
                    pointer-events: auto;
                    transition: all 0.125s ease 0s;
                    width: 100%;
                    min-height: 370px;
                    margin-top: 19px;
                }
                .MiddleAligner {
                    align-self: stretch;
                    display: flex;
                    max-width: 19px;
                    min-width: 19px;
                    pointer-events: none;
                    z-index: 50;
                }
                .NFTPreviewInnerCover {
                    display: flex;
                    padding: 0px;
                    border-radius: 10px;
                    -webkit-box-align: center;
                    align-items: center;
                    width: 378.79px;
                    height: 100%;
                    -webkit-box-pack: center;
                    justify-content: center;
                }
                .NFTPreviewer {
                    background: white;
                    border-radius: 10px;
                    box-shadow: rgb(0 0 0 / 40%) 0px 10px 30px;
                    max-height: calc(100vh - 100px);
                    overflow: hidden;
                    padding-top: 0px;
                    pointer-events: auto;
                    position: relative;
                    transition: all 0.125s ease 0s;
                    z-index: 50;
                    width: 378.79px;
                    // height: 370px;
                    height: 100%;
                    display: flex;
                    padding: 0px;
                    -webkit-box-align: center;
                    align-items: center;
                    -webkit-box-pack: start;
                    justify-content: flex-start;
                    flex-direction: column !important;
                }
                .ModalBoxAreaMain {
                    width: 100%;
                    display: flex;
                    padding: 0px;
                    justify-content: center;
                    align-items: flex-start;
                    flex-direction: row;
                }
                .insideBox {
                    cursor: auto;
                    display: flex;
                    height: 100%;
                    justify-content: center;
                    max-height: 100%;
                    width: 100%;
                    overflow-y: auto;
                }
                .middleBoxIn {
                    align-items: flex-start;
                    transform: translateY(0vh) scale(1) translateZ(0px);
                    // align-self: center;
                    background: transparent;
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    height: 100%;
                    margin: 10px 72px;
                    pointer-events: none;
                    width: 100%;
                    z-index: 40;
                }
                .closeModalBox {
                    cursor: pointer;
                    height: 27px;
                    opacity: 1;
                    overflow: visible;
                    padding: 30px 50px;
                    pointer-events: auto;
                    position: fixed;
                    top: 0px;
                    right: 0px;
                    width: 27px;
                    transition: all 0.125s ease 0s;
                    z-index: 100;
                }
                .closeModalBoxMobile {
                    display: none;
                }
                .gBlurUpper {
                    background-color: rgb(0, 0, 0, 0.7);
                    cursor: default;
                    left: -50vh;
                    opacity: 0.8;
                    pointer-events: auto;
                    position: fixed;
                    top: 0px;
                    transition: all 1s ease;
                    opacity: 1;
                    transition: opacity 0.3s ease-in;
                    width: 250vw;
                    height: 100%;
                    z-index: 1;
                }
                .blurUp {
                    align-items: center;
                    align-self: center;
                    display: flex;
                    height: 100%;
                    justify-content: center;
                    left: 0px;
                    overflow: hidden;
                    position: fixed;
                    top: 0px;
                    transition: all 1s ease;
                    opacity: 1;
                    transition: opacity 0.3s ease-in;
                    width: 100%;
                    will-change: transform;
                    z-index: 40;
                    animation: 0.3s linear 0s 1 normal none running fadeIn;
                }
                .token-attribute {
                    background: #f7f7f7;
                    border-radius: 4px;
                    padding: 1rem;
                    padding: 19px 24px;
                    gap: 10px;
                    width: 128px;
                    height: 81px;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                }
                .ipfs-links {
                    cursor: pointer;
                    font-size: 10px;
                    font-weight: 800;
                }
                .Polaris-Icon {
                    display: flex;
                }
                @media (max-width: 1200px) {
                    .closeModalBox {
                        padding: 19px 44px;
                        position: fixed;
                        right: 0px;
                        top: 0px;
                    }
                    .NFTPreviewInnerCover {
                        width: 328.79px;
                    }
                    .NFTPreviewer {
                        width: 328.79px;
                    }
                    .MiddleAligner {
                        max-width: 14px;
                        min-width: 14px;
                    }
                    .ModalDetailBoxStart {
                        max-width: 603px;
                        min-height: 320px;
                        min-width: 603px;
                    }
                    .BenefitsBoxStart {
                        max-width: 948px;
                        min-width: 948px;
                        margin-top: 14px;
                    }
                    .attributeMore {
                        display: flex;
                        flex-direction: column;
                    }
                }
                @media (max-width: 960px) {
                    .insideBox {
                        overflow-y: auto;
                    }
                    .spacing-sm {
                        display: block;
                    }
                    .BenefitsBoxStart {
                        display: none;
                    }
                    .box-section-area-sm {
                        margin-top: 20px;
                        margin-bottom: 20px;
                        display: block;
                        width: 100%;
                    }
                    .attributeMore {
                        display: none;
                    }
                    .NFTPreviewInnerCover {
                        width: 101%;
                    }
                    .box-container-section {
                        display: block;
                    }
                    .flex-block-switch {
                        display: block;
                        margin-bottom: 3rem;
                    }
                    .grey-button-see-more {
                        width: 100%;
                        background: #f7f7f7;
                        justify-content: center;
                        display: flex;
                        color: #fff;
                        padding: 0px;
                        font-weight: 500;
                        font-size: 12px;
                        height: 28px;
                        align-items: center;
                        border-radius: 4px;
                        color: #959595;
                    }
                    .ModalDetailBoxStartInsider {
                        margin-top: 0px;
                    }
                    .blurUp {
                        overflow: clip scroll;
                    }
                    .attribute-box-area {
                        margin-right: 0rem;
                    }
                    .cancel-icon-main {
                        display: none;
                    }
                    .NFTPreviewer {
                        width: 100%;
                        // height: 425px;
                        margin-left: 0px;
                        margin-top: 60px;
                        max-height: none;
                        padding-top: 0px;
                    }
                    .closeModalBox {
                        display: none;
                    }
                    .closeModalBoxMobile {
                        cursor: pointer;
                        height: 27px;
                        opacity: 1;
                        width: 100%;
                        transition: all 0.125s ease 0s;
                        z-index: 100;
                        position: fixed;
                        top: 10px;
                        display: flex;
                        justify-content: flex-end;
                    }
                    .MiddleAligner {
                        display: block;
                        height: 19px;
                        margin-bottom: 0px;
                        max-width: none;
                        pointer-events: auto;
                        width: 100%;
                    }
                    .middleBoxIn {
                        box-sizing: border-box;
                        display: block;
                        flex-direction: column;
                        margin: 0px;
                        max-width: 460px;
                        width: 100%;
                    }
                    .ModalDetailBoxStart {
                        max-height: none;
                        min-width: 100%;
                        width: 100%;
                    }
                    .ModalBoxAreaMain {
                        flex-direction: column;
                        align-items: center;
                    }
                    .blurUp > div {
                        width: 100%;
                        position: absolute;
                    }
                    .gBlurUpper {
                        left: 0;
                    }

                    .marketplace_areas {
                        margin-bottom: 3rem;
                        width: 100%;
                        margin-top: 3rem;
                    }
                }
            `}</style>
        </React.Fragment>;
}